// Type definitions
export type SizeVariant = 'S' | 'M' | 'L';

/**
 * Capitalize the first letter of a string and lowercase the rest
 * @param string - The string to capitalize
 * @returns Capitalized string
 */
export const capitalize = (string: string): string =>
  `${string.charAt(0).toUpperCase()}${string.slice(1).toLowerCase()}`;

/**
 * Remove 'px' suffix from a string value and convert to number.
 * If input is already a number, returns it unchanged.
 * If input is null/undefined, returns it unchanged.
 */
export function removePx(value: string | number): number;
export function removePx(value: null | undefined): null | undefined;
export function removePx(
  value: string | number | null | undefined
): number | null | undefined {
  if (typeof value !== 'string') return value;
  return Number.parseFloat(value.replace(/px$/i, ''));
}

/**
 * Generate CSS calc expression for radius based on size variant
 * @param originalValue - The original radius value (typically CSS value)
 * @param size - Size variant ('S', 'M', or 'L')
 * @returns CSS calc expression string
 */
export const toRadius = (
  originalValue: string | number,
  size: SizeVariant = 'L'
): string => {
  switch (size) {
    case 'S':
      return `calc(${originalValue} / 8)`;
    case 'M':
      return `calc(${originalValue} / 4)`;
    case 'L':
      return `calc(${originalValue} / 2)`;
    default: {
      // Exhaustive check - TypeScript will error if we miss a case
      const exhaustiveCheck: never = size;
      throw new Error(`Unhandled size variant: ${exhaustiveCheck}`);
    }
  }
};

/**
 * Add a suffix to a value
 * @param value - The base value
 * @param suffix - The suffix to append
 * @returns String with suffix appended
 */
export const addSuffix = (value: string | number, suffix: string): string =>
  `${value}${suffix}`;

/**
 * Add a 'px' suffix to a value
 * @param value - The base value
 * @returns String with 'px' suffix appended
 */
export const addPx = (value: string | number): string => addSuffix(value, 'px');
